import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { Toast } from '@/shared/components/toast/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { BadgeModule } from 'primeng/badge';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { LayoutService } from '@/layout/service/layout.service';
import { RefisPortariaService } from '@/core/api/sicom-api/financeiro/arrecadacao/refis-portaria/refis-portaria.service';
import { RefisPortariaRequest, RefisPortariaResponse } from '@/core/api/sicom-api/financeiro/arrecadacao/refis-portaria/refis-portaria.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
import { CardSortPills, CardSortOption } from '@/shared/components/layout/card-sort-pills/card-sort-pills';
interface SortOpcao {
    label: string;
    field: 'nm_portaria' | 'dt_inicio' | 'dt_fim' | 'vr_custo';
    direction: 'asc' | 'desc';
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-refis',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        FormsModule,
        ButtonModule,
        DialogModule,
        TableModule,
        PaginatorModule,
        SelectModule,
        InputTextModule,
        InputNumberModule,
        DatePickerModule,
        Toast,
        ConfirmDialogModule,
        TooltipModule,
        BadgeModule,
        CardContent,
        CardContainer,
        SelectEstado,
        TableViewSwitcherComponent,
        CanDirective,
        FiltrosBar,
        FiltroCampo,
        CardSortPills
    ],
    providers: [MessageService, ConfirmationService],
    templateUrl: './refis.html',
    styleUrl: './refis.scss'
})
/** Configuração de programas de Refis (parcelamento/descontos). */
export class Refis implements OnInit {
    private readonly refisService = inject(RefisPortariaService);
    private readonly layoutService = inject(LayoutService);
    private readonly messageService = inject(MessageService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly permissionService = inject(PermissionService);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly rolePermissaoEditar = PERMISSOES.configuracoes.refis.editar;
    protected readonly rolePermissaoExcluir = PERMISSOES.configuracoes.refis.excluir;

    protected readonly uf = signal<string>('');

    /** Regional não escolhe UF: o backend impõe a dele, e o select reflete isso em vez de mentir. */
    protected readonly ufTravada = computed(() => !this.permissionService.isCfm);

    protected readonly portarias = signal<RefisPortariaResponse[]>([]);
    protected readonly carregando = signal(false);
    protected readonly salvando = signal(false);

    // ── Visualização, filtros e ordenação (padrão grid-pagamento) ──
    protected readonly currentView = signal<'table' | 'card'>(this.layoutService.getPresentationType());
    protected readonly filtroPortaria = signal<string>('');
    protected readonly filtroAplicado = signal<string>('');
    // Combo de UF nos filtros — sem efeito por enquanto (funcionalidade futura).
    protected readonly filtroUf = signal<string>('');
    protected readonly sortField = signal<SortOpcao['field']>('dt_inicio');
    protected readonly sortOrder = signal<1 | -1>(-1);

    /**
     * Critérios das pills do modo card. Um por campo: a direção vem do clique na pill,
     * e não de duas opções separadas como no seletor que havia aqui.
     */
    protected readonly sortOpcoes: CardSortOption<SortOpcao['field']>[] = [
        { field: 'dt_inicio', label: 'Início' },
        { field: 'dt_fim', label: 'Fim' },
        { field: 'nm_portaria', label: 'Portaria' },
        { field: 'vr_custo', label: 'Valor' }
    ];

    protected readonly portariasView = computed<RefisPortariaResponse[]>(() => {
        const termo = this.filtroAplicado().trim().toLowerCase();
        const field = this.sortField();
        const order = this.sortOrder();

        const filtradas = termo ? this.portarias().filter((p) => (p.nm_portaria ?? '').toLowerCase().includes(termo)) : [...this.portarias()];

        return filtradas.sort((a, b) => {
            const va = a[field];
            const vb = b[field];
            let cmp: number;
            if (typeof va === 'number' && typeof vb === 'number') {
                cmp = va - vb;
            } else {
                cmp = String(va ?? '').localeCompare(String(vb ?? ''), 'pt-BR');
            }
            return cmp * order;
        });
    });

    // ── Paginação (padrão grid-pagamento) ──────────────────────────
    protected readonly first = signal(0);
    protected readonly rows = signal(10);
    protected readonly totalRecords = computed(() => this.portariasView().length);
    protected readonly portariasPagina = computed<RefisPortariaResponse[]>(() => {
        const inicio = this.first();
        return this.portariasView().slice(inicio, inicio + this.rows());
    });

    // ── Estado do modal de edição ──────────────────────────────────
    protected readonly modalAberto = signal(false);
    protected readonly editId = signal<number | null>(null);
    protected readonly formUf = signal<string>('');
    protected readonly formPortaria = signal<string>('');
    protected readonly formDtInicio = signal<Date | null>(null);
    protected readonly formDtFim = signal<Date | null>(null);
    protected readonly formVrCusto = signal<number | null>(null);

    ngOnInit(): void {
        // Regional: o escopo já dá a UF, e vence o `obterUf()` — que resolve por outra cascata,
        // pode voltar vazio e só responde depois de a tela estar de pé. Com o select travado não
        // haveria como corrigir na mão: o modal abriria vazio e o filtro exibiria "BR".
        const ufOperador = this.ufOperador();
        if (ufOperador) {
            this.uf.set(ufOperador);
            this.filtroUf.set(ufOperador);
            this.carregar();
            return;
        }

        // Resolve a UF do usuário (best-effort) para sugerir como padrão nos combos
        // de busca e do modal, e então carrega a lista já filtrada por essa UF.
        this.refisService
            .obterUf()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (uf) => {
                    const ufNorm = (uf ?? '').trim().toUpperCase();
                    this.uf.set(ufNorm);
                    // Sem UF resolvida → filtro inicia em BR (mostra todas).
                    this.filtroUf.set(ufNorm || 'BR');
                    this.carregar();
                },
                error: () => {
                    this.filtroUf.set('BR');
                    this.carregar();
                }
            });
    }

    /** UF imposta ao operador regional; `''` para o CFM, que escolhe livremente. */
    private ufOperador(): string {
        return this.ufTravada() ? (this.permissionService.ufUsuario ?? '').trim().toUpperCase() : '';
    }

    protected get tituloModal(): string {
        return this.editId() === null ? 'Nova Portaria' : 'Editar Portaria';
    }

    protected formatarData(iso?: string | null): string {
        if (!iso) return '';
        const [ano, mes, dia] = iso.substring(0, 10).split('-');
        return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso;
    }

    protected formatarMoeda(valor?: number | null): string {
        if (valor == null) return '';
        return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    // ── Paginação ──────────────────────────────────────────────────
    protected onPageChange(event: PaginatorState): void {
        this.first.set(event.first ?? 0);
        this.rows.set(event.rows ?? this.rows());
    }

    // ── Filtros / ordenação ────────────────────────────────────────
    protected aplicarFiltros(): void {
        this.filtroAplicado.set(this.filtroPortaria());
        this.first.set(0);
        // UF é filtrada no backend (BR/vazio = todas); o texto é filtrado no cliente.
        this.carregar();
    }

    protected limparFiltros(): void {
        this.filtroPortaria.set('');
        this.filtroAplicado.set('');
        // Regional: limpar não destrava a UF — ela volta para a dele, nunca para "BR" (todas).
        this.filtroUf.set(this.ufOperador() || this.uf() || 'BR');
        this.sortField.set(this.sortOpcoes[0].field);
        this.sortOrder.set(-1);
        this.first.set(0);
        this.carregar();
    }

    /** Trocar a ordenação volta para a primeira página: a página atual pode nem existir depois. */
    protected onSortCardChange(): void {
        this.first.set(0);
    }

    protected onSortChange(event: { field?: string | string[] | null; order?: number | null }): void {
        const field = (Array.isArray(event.field) ? event.field[0] : event.field) as SortOpcao['field'] | undefined;
        if (!field) return;
        const order: 1 | -1 = event.order === -1 ? -1 : 1;
        this.sortField.set(field);
        this.sortOrder.set(order);
        this.first.set(0);
    }

    // ── CRUD ───────────────────────────────────────────────────────
    protected abrirNova(): void {
        const hoje = new Date();
        // Último dia do mês atual: dia 0 do mês seguinte.
        const ultimoDiaMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

        this.editId.set(null);
        this.formUf.set(this.ufOperador() || this.uf());
        this.formPortaria.set('');
        this.formDtInicio.set(hoje);
        this.formDtFim.set(ultimoDiaMes);
        this.formVrCusto.set(null);
        this.modalAberto.set(true);
    }

    protected abrirEditar(portaria: RefisPortariaResponse): void {
        this.editId.set(portaria.id);
        this.formUf.set(portaria.sg_uf ?? this.uf());
        this.formPortaria.set(portaria.nm_portaria ?? '');
        this.formDtInicio.set(this.parseIso(portaria.dt_inicio));
        this.formDtFim.set(this.parseIso(portaria.dt_fim));
        this.formVrCusto.set(portaria.vr_custo ?? null);
        this.modalAberto.set(true);
    }

    protected fecharModal(): void {
        this.modalAberto.set(false);
    }

    protected salvar(): void {
        const sgUf = this.formUf();
        const portaria = this.formPortaria().trim();
        const dtInicio = this.formDtInicio();
        const dtFim = this.formDtFim();
        const vrCusto = this.formVrCusto();

        if (!sgUf || sgUf === 'BR') {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione a UF.' });
            return;
        }
        if (!portaria || !dtInicio || !dtFim || vrCusto == null) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Preencha portaria, datas e valor de custo operacional.' });
            return;
        }
        if (portaria.length > 20) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'A portaria deve ter no máximo 20 caracteres.' });
            return;
        }
        if (dtFim < dtInicio) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'A data fim deve ser maior ou igual à data início.' });
            return;
        }

        const request: RefisPortariaRequest = {
            id: this.editId() ?? undefined,
            sg_uf: sgUf,
            nm_portaria: portaria,
            dt_inicio: this.toIso(dtInicio),
            dt_fim: this.toIso(dtFim),
            vr_custo: vrCusto
        };

        this.salvando.set(true);
        this.refisService
            .salvar(request)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.salvando.set(false))
            )
            .subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Portaria salva com sucesso.' });
                    this.modalAberto.set(false);
                    this.first.set(0);
                    // Recarrega respeitando o filtro de UF atual da busca.
                    this.carregar();
                },
                error: () => undefined
            });
    }

    protected confirmarExclusao(portaria: RefisPortariaResponse): void {
        this.confirmationService.confirm({
            header: 'Remover portaria',
            message: `Deseja realmente remover a portaria "${portaria.nm_portaria}"?`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Remover',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => this.excluir(portaria.id)
        });
    }

    private excluir(id: number): void {
        this.refisService
            .excluir(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.portarias.update((lista) => lista.filter((p) => p.id !== id));
                    if (this.first() >= this.totalRecords()) {
                        this.first.set(Math.max(0, this.totalRecords() - this.rows()));
                    }
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Portaria removida com sucesso.' });
                },
                error: () => undefined
            });
    }

    private carregar(): void {
        this.carregando.set(true);
        const uf = this.filtroUf();
        const ufParam = !uf || uf === 'BR' ? undefined : uf;
        this.refisService
            .listar(ufParam)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregando.set(false))
            )
            .subscribe({
                next: (lista) => {
                    this.portarias.set(lista);
                    this.first.set(0);
                },
                error: () => this.portarias.set([])
            });
    }

    /** Converte Date local para ISO yyyy-MM-dd sem deslocamento de fuso. */
    private toIso(data: Date): string {
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const dia = String(data.getDate()).padStart(2, '0');
        return `${ano}-${mes}-${dia}`;
    }

    /** Converte ISO yyyy-MM-dd em Date local (evita off-by-one de fuso). */
    private parseIso(iso?: string | null): Date | null {
        if (!iso) return null;
        const [ano, mes, dia] = iso.substring(0, 10).split('-').map(Number);
        return ano && mes && dia ? new Date(ano, mes - 1, dia) : null;
    }
}
